/**
 * @name Router Configuration
 * @description Configuração principal das rotas usando React Router DOM v7
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

// Layouts
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { RootLayout } from './layouts/RootLayout';

// Pages
import { ErrorPage, LoginPage, MinhaContaPage, NotFoundPage, ResetPasswordPage } from '@/components/pages';

// Páginas de Gestão Financeira
import SaldoPage from '@/components/pages/gestao-financeira/creditos/SaldoPage';
import ProtectedDashboardPage from '@/components/pages/gestao-financeira/dashboard/ProtectedDashboardPage';
import { DependentesPage } from '@/components/pages/gestao-financeira/dependentes';
import HistoricoPage from '@/components/pages/gestao-financeira/historico/HistoricoPage';

// Páginas de Integração SSO
import IntegrationLoginPage from '@/components/pages/Auth/SsoIntegration/IntegrationLoginPage';

// Route Guards
import { ProtectedRoute } from './guards/ProtectedRoute';
import { PublicRoute } from './guards/PublicRoute';

/**
 * Configuração das rotas da aplicação
 */
export const router = createBrowserRouter([
     {
          path: '/',
          element: <RootLayout />,
          errorElement: <ErrorPage />,
          children: [
               // Rotas públicas (auth)
               {
                    path: 'auth',
                    element: (
                         <PublicRoute>
                              <AuthLayout />
                         </PublicRoute>
                    ),
                    children: [
                         {
                              path: 'login',
                              element: <LoginPage />,
                         },
                         {
                              path: 'reset-password',
                              element: <ResetPasswordPage />,
                         },
                         {
                              path: 'integration',
                              element: <IntegrationLoginPage />,
                         },
                    ],
               },

               // Rota especial para capturar URLs de integração externa como /login?ano_letivo=X&matricula=Y
               {
                    path: 'login',
                    element: <IntegrationLoginPage />,
               },

               // Rotas protegidas (app)
               {
                    path: '',
                    element: (
                         <ProtectedRoute>
                              <AppLayout />
                         </ProtectedRoute>
                    ),
                    children: [
                         {
                              index: true,
                              element: <ProtectedDashboardPage />,
                         },
                         {
                              path: 'minha-conta',
                              element: <MinhaContaPage />,
                         },
                         // Rotas de Créditos/Saldo
                         {
                              path: 'creditos',
                              element: <SaldoPage />,
                         },
                         {
                              path: 'saldo',
                              element: <SaldoPage />,
                         },
                         {
                              path: 'creditos/transferir',
                              element: <DependentesPage />,
                         },
                         // Rotas de Dependentes
                         {
                              path: 'dependentes',
                              element: <DependentesPage />,
                         },
                         // Rotas de Histórico
                         {
                              path: 'historico-compras',
                              element: <HistoricoPage />,
                         },
                         {
                              path: 'historico',
                              element: <HistoricoPage />,
                         },
                    ],
               },
          ],
     },

     // Página 404
     {
          path: '*',
          element: <NotFoundPage />,
     },
]);

/**
 * Provider principal do router
 */
export const AppRouter: React.FC = () => {
     return <RouterProvider router={router} />;
};
